// Generic UI backstack manager
// - Pushes a browser history state for each opened UI (dialog, menu, etc.)
// - On browser back (popstate), closes the most recently opened UI
// - When a UI closes programmatically, consumes the history entry to avoid navigation
//
// Usage:
// const uiBack = useUiBackstack()
// uiBack.bindDialog(someBooleanRef, { id: 'spellList' })
// uiBack.bindDialog(otherBooleanRef, { id: 'spellDetails' })
//
// Also supports manual push/pop via uiBack.push(() => closeFn(), { id })

import { ref, watch, onBeforeUnmount } from 'vue'
import { safePushState } from '@/utils/historyState'

let globalListenerAttached = false
let globalManagers = new Set()
// Global stack of all UI layers across managers in open order
const globalStack = [] // Array<{ id: string, onClose: Function }>
// Manage scroll positions per entry without altering global scrollRestoration
let originalScrollRestoration = null
let globalHandlingPopstate = false
let _consumeOnlyBackCount = 0
let _popstateToken = 0
const consumeGlobalKey = '__UiBackstackConsumeOnlyCount'
const externalConsumeKey = '__DialogBackstackConsumeOnlyCount'

function getScrollContainer() {
  try {
    if (typeof document !== 'undefined') {
      // Prefer the scrollingElement when available (handles Quasar layouts)
      return document.scrollingElement || document.documentElement || document.body
    }
  } catch {}
  return null
}

function getScrollPosition() {
  try {
    const el = getScrollContainer()
    if (el) return { x: el.scrollLeft || 0, y: el.scrollTop || 0 }
  } catch {}
  // Fallback to window
  try {
    return {
      x: (typeof window.scrollX === 'number' ? window.scrollX : window.pageXOffset) || 0,
      y: (typeof window.scrollY === 'number' ? window.scrollY : window.pageYOffset) || 0
    }
  } catch {}
  return { x: 0, y: 0 }
}

function setScrollPosition(x, y) {
  try {
    const el = getScrollContainer()
    if (el) {
      const maxY = Math.max(0, (el.scrollHeight || 0) - (el.clientHeight || 0))
      const maxX = Math.max(0, (el.scrollWidth || 0) - (el.clientWidth || 0))
      const tx = Math.min(Math.max(x || 0, 0), maxX)
      const ty = Math.min(Math.max(y || 0, 0), maxY)
      if (typeof el.scrollTo === 'function') el.scrollTo(tx, ty)
      else {
        el.scrollLeft = tx
        el.scrollTop = ty
      }
      return
    }
  } catch {}
  try {
    const doc = document && (document.scrollingElement || document.documentElement || document.body)
    const maxY = doc ? Math.max(0, (doc.scrollHeight || 0) - (doc.clientHeight || 0)) : 0
    const maxX = doc ? Math.max(0, (doc.scrollWidth || 0) - (doc.clientWidth || 0)) : 0
    const tx = Math.min(Math.max(x || 0, 0), maxX)
    const ty = Math.min(Math.max(y || 0, 0), maxY)
    window.scrollTo(tx, ty)
  } catch {}
}

export function useUiBackstack() {
  const stack = ref([]) // Array<{ id: string, onClose: Function }>
  const idCounters = {}

  function nextId(prefix = 'ui') {
    idCounters[prefix] = (idCounters[prefix] || 0) + 1
    return `${prefix}-${idCounters[prefix]}`
  }

  function push(onClose, opts = {}) {
    const id = opts.id || nextId('ui')
    attachGlobal()
    // Push a history state per opened UI layer
    safePushState({ uiBackstack: 'ui-layer', id })
    const rec = { id, onClose, _owner: api, _ts: Date.now() }
    stack.value.push(rec)
    globalStack.push(rec)
    try {
      window.__UiBackstackTopTs = rec._ts || Date.now()
      window.__UiBackstackCount = globalStack.length
    } catch {}
    return id
  }

  function pop() {
    const top = globalStack.pop()
    if (!top) return
    try {
      const nextTop = globalStack[globalStack.length - 1]
      window.__UiBackstackTopTs = (nextTop && nextTop._ts) || 0
      window.__UiBackstackCount = globalStack.length
    } catch {}
    // Also remove from local stack if present; prefer last match in case of duplicate ids
    let idx = -1
    for (let i = stack.value.length - 1; i >= 0; i--) {
      if (stack.value[i].id === top.id) {
        idx = i
        break
      }
    }
    if (idx >= 0) stack.value.splice(idx, 1)
    try {
      top.onClose && top.onClose()
    } catch {}
  }

  function handlePopstate(ev) {
    try {
      const externalCount = Number(window[externalConsumeKey] || 0)
      if (externalCount > 0) {
        window[externalConsumeKey] = Math.max(0, externalCount - 1)
        return
      }
    } catch {}
    // If a programmatic dialog was opened after this inline layer, let
    // the dialogBackstack handle this Back press (prevent double-close).
    try {
      const programTs = Number(window.__QDialogBackstackTopTs || 0)
      const inlineDialogTs = Number(window.__QInlineDialogTopTs || 0)
      const selectTs = Number(window.__QSelectTopTs || 0)
      const inlineTs = Number(window.__UiBackstackTopTs || 0)
      const topTs = Math.max(programTs, inlineDialogTs, selectTs, inlineTs)
      if (topTs > 0 && topTs !== inlineTs) return
    } catch {}
    const token = ++_popstateToken
    globalHandlingPopstate = true
    let resetOverflowAnchor = null
    try {
      // If a programmatic consume-only back was requested, skip closing
      if (_consumeOnlyBackCount > 0) {
        _consumeOnlyBackCount = Math.max(0, _consumeOnlyBackCount - 1)
        try {
          const current = Number(window[consumeGlobalKey] || 0)
          if (current > 0) window[consumeGlobalKey] = Math.max(0, current - 1)
        } catch {}
        return
      }

      // Disable scroll anchoring temporarily to avoid browser adjusting position on layout changes
      try {
        const el =
          document && (document.scrollingElement || document.documentElement || document.body)
        if (el && el.style) {
          const prev = el.style.overflowAnchor || ''
          el.style.overflowAnchor = 'none'
          resetOverflowAnchor = () => {
            try {
              el.style.overflowAnchor = prev
            } catch {}
          }
        }
      } catch {}

      // Also blur active element to prevent focus auto-scrolling
      try {
        const ae = document && document.activeElement
        if (ae && typeof ae.blur === 'function') ae.blur()
      } catch {}

      if (globalStack.length > 0) {
        const top = globalStack.pop()
        try {
          window.__UiBackstackCount = globalStack.length
        } catch {}
        // Remove from owning local stack if present (remove last matching id to avoid duplicates issues)
        try {
          const owner = top && top._owner
          if (owner && owner._stack && owner._stack.value) {
            let lidx = -1
            for (let i = owner._stack.value.length - 1; i >= 0; i--) {
              if (owner._stack.value[i].id === top.id) {
                lidx = i
                break
              }
            }
            if (lidx >= 0) owner._stack.value.splice(lidx, 1)
          } else {
            // Fallback: try to remove by id from any manager
            globalManagers.forEach((mgr) => {
              try {
                let l = -1
                for (let i = mgr._stack.value.length - 1; i >= 0; i--) {
                  if (mgr._stack.value[i].id === top.id) {
                    l = i
                    break
                  }
                }
                if (l >= 0) mgr._stack.value.splice(l, 1)
              } catch {}
            })
          }
        } catch {}
        try {
          top.onClose && top.onClose()
        } catch {}

        // Allow layout to settle, then restore overflow-anchor
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (resetOverflowAnchor) resetOverflowAnchor()
          }, 200)
        })

        return
      }

      // Nothing to close; just restore overflow-anchor soon
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (resetOverflowAnchor) resetOverflowAnchor()
        }, 200)
      })
    } finally {
      const clearHandling = () => {
        if (_popstateToken === token) globalHandlingPopstate = false
      }
      try {
        if (typeof queueMicrotask === 'function') {
          queueMicrotask(clearHandling)
        } else {
          Promise.resolve().then(clearHandling)
        }
      } catch {
        clearHandling()
      }
    }
  }

  function attachGlobal() {
    if (globalListenerAttached) return
    try {
      window.addEventListener('popstate', handlePopstate)
      globalListenerAttached = true
      // Prevent browser auto scroll restore while our backstack is active
      try {
        if (typeof window.history.scrollRestoration !== 'undefined') {
          originalScrollRestoration = window.history.scrollRestoration
          window.history.scrollRestoration = 'manual'
        }
      } catch {}
    } catch {}
  }

  function detachGlobalIfUnused() {
    globalManagers.delete(api)
    if (
      globalManagers.size === 0 &&
      globalListenerAttached &&
      _consumeOnlyBackCount === 0 &&
      Number(window[consumeGlobalKey] || 0) === 0
    ) {
      try {
        window.removeEventListener('popstate', handlePopstate)
      } catch {}
      globalListenerAttached = false
      // Restore previous browser scroll restoration behavior
      try {
        if (
          typeof window.history.scrollRestoration !== 'undefined' &&
          originalScrollRestoration != null
        ) {
          window.history.scrollRestoration = originalScrollRestoration
        }
      } catch {}
      originalScrollRestoration = null
    }
  }

  function bindDialog(modelRef, opts = {}) {
    const id = opts.id || nextId('dlg')
    let isOpen = false
    let hasEntry = false
    const thresholdMs = 250
    const shouldConsumeOnClose = () => {
      try {
        if (typeof opts.consumeOnClose === 'function') return !!opts.consumeOnClose()
        if (typeof opts.shouldConsumeOnClose === 'function') return !!opts.shouldConsumeOnClose()
      } catch {}
      if (opts.consumeOnClose === false) return false
      if (opts.skipConsumeOnClose === true) return false
      return true
    }

    watch(
      modelRef,
      (val, oldVal) => {
        if (val && !isOpen) {
          // Opened
          isOpen = true
          const inlineDialogTs = Number(window.__QInlineDialogTopTs || 0)
          const now = Date.now()
          if (inlineDialogTs && now - inlineDialogTs < thresholdMs) {
            hasEntry = false
            return
          }
          hasEntry = true
          push(
            () => {
              // Custom close for components that don't use a boolean v-model
              if (typeof opts.onClose === 'function') {
                try {
                  opts.onClose()
                } catch {}
              } else {
                // Default: set v-model false
                if (modelRef.value) modelRef.value = false
              }
            },
            { id }
          )
        } else if (!val && isOpen) {
          // Closed programmatically; if not due to popstate, consume history
          isOpen = false
          if (!hasEntry) {
            hasEntry = false
            return
          }
          hasEntry = false
          // Remove our own entry from both stacks
          let lidx = -1
          for (let i = stack.value.length - 1; i >= 0; i--) {
            if (stack.value[i].id === id) {
              lidx = i
              break
            }
          }
          if (lidx >= 0) stack.value.splice(lidx, 1)
          let gidx = -1
          for (let i = globalStack.length - 1; i >= 0; i--) {
            if (globalStack[i].id === id) {
              gidx = i
              break
            }
          }
          if (gidx >= 0) {
            globalStack.splice(gidx, 1)
            try {
              const nextTop = globalStack[globalStack.length - 1]
              window.__UiBackstackTopTs = (nextTop && nextTop._ts) || 0
              window.__UiBackstackCount = globalStack.length
            } catch {}
          }
          // Consume the history entry that was created on open so Back won't close another layer
          if (!globalHandlingPopstate && shouldConsumeOnClose()) {
            consumeHistoryBack()
          }
        }
      },
      { immediate: false }
    )

    attachGlobal()

    return {
      unbind() {
        // Ensure removal from stack if present
        let idx = -1
        for (let i = stack.value.length - 1; i >= 0; i--) {
          if (stack.value[i].id === id) {
            idx = i
            break
          }
        }
        if (idx >= 0) stack.value.splice(idx, 1)
      }
    }
  }

  function consumeHistoryBack() {
    try {
      _consumeOnlyBackCount += 1
      const prev = Number(window[consumeGlobalKey] || 0)
      window[consumeGlobalKey] = prev + 1
    } catch {}
    try {
      window.history.back()
    } catch {
      _consumeOnlyBackCount = Math.max(0, _consumeOnlyBackCount - 1)
      try {
        const current = Number(window[consumeGlobalKey] || 0)
        window[consumeGlobalKey] = Math.max(0, current - 1)
      } catch {}
    }
  }

  onBeforeUnmount(() => {
    detachGlobalIfUnused()
  })

  const api = {
    push,
    pop,
    bindDialog,
    size: () => stack.value.length,
    _stack: stack
  }
  // Register this manager globally for coordinated stack updates
  try {
    globalManagers.add(api)
  } catch {}
  return api
}
